import React, { useEffect, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { apiFetchAuthed } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import Panel from '../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

// "Next chargers to provision" — a READ-ONLY preview of the next N auto-named
// SmartChargers (case ID + VPN IP each), from GET /api/assets/preview-queue.
// It reserves nothing and — the standing constraint — Firefleet never creates the
// OpenVPN clients here. This is a worksheet: it tells whoever is building the next
// batch what the cases will be named and which VPN IPs to hand to OpenVPN.

interface QueueItem {
  sequence_number: number;
  display_code: string;
  vpn_ip: string | null;
  vpn_available: boolean;
}

const COUNT = 5;

interface QueueMeta { copy: (text: string) => void }

const COLUMNS: ColumnDef<QueueItem, any>[] = [
  {
    id: 'display_code',
    header: 'Case ID',
    cell: (info) => <span className="font-mono text-on-surface">{info.row.original.display_code}</span>,
  },
  {
    id: 'sequence_number',
    header: 'Seq',
    cell: (info) => <span className="tabular-nums text-on-surface-variant">{info.row.original.sequence_number}</span>,
  },
  {
    id: 'vpn_ip',
    header: 'VPN IP',
    cell: (info) => {
      const i = info.row.original;
      return i.vpn_ip
        ? <span className="font-mono text-on-surface">{i.vpn_ip}</span>
        : <span className="font-mono text-amber-300">subnet full</span>;
    },
  },
  {
    id: 'copy',
    header: () => <span className="sr-only">Copy</span>,
    cell: (info) => {
      const i = info.row.original;
      const m = info.table.options.meta as QueueMeta;
      return (
        <button
          type="button"
          onClick={() => m.copy(i.vpn_ip ? `${i.display_code}\t${i.vpn_ip}` : i.display_code)}
          className={buttonClass('quiet', 'sm')}
        >Copy</button>
      );
    },
  },
];

export default function NextChargersCard() {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guard against a resolve after the tab is switched away mid-fetch.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await apiFetchAuthed(
        `/assets/preview-queue?type_key=smart_charger&count=${COUNT}`
      )) as { items: QueueItem[] };
      if (!mounted.current) return;
      setItems(res.items || []);
      setLoaded(true);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Failed to load preview');
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  // Lazy — only hit the API the first time the card is expanded.
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) load();
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const copyAll = () => {
    const lines = items.map((i) => `${i.display_code}\t${i.vpn_ip ?? '(subnet full)'}`);
    copy(lines.join('\n'));
  };

  return (
    <Panel label="Next chargers to provision" padding="none" className="mb-4">
      <button
        type="button" onClick={toggle} aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
      >
        <span className="flex items-center gap-2 min-w-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            className={`text-on-surface-variant transition-transform ${open ? 'rotate-90' : ''}`}>
            <path d="M9 18l6-6-6-6" />
          </svg>
          <span className="font-semibold text-white truncate">Next chargers to provision</span>
          <span className="text-xs text-on-surface-variant flex-shrink-0">preview only</span>
        </span>
      </button>

      <Reveal show={open}>
        <div className="px-4 pb-4">
          <FadeIn key={loading ? 'loading' : error ? 'error' : items.length > 0 ? 'items' : 'empty'}>
          {loading && (
            <SkeletonGroup label="Loading the preview" className="space-y-1 py-2">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </SkeletonGroup>
          )}
          {error && <Alert variant="text" tone="error" className="py-2">{error}</Alert>}

          {!loading && !error && items.length > 0 && (
            <>
              <DataTable<QueueItem>
                data={items}
                columns={COLUMNS}
                getRowId={(i) => String(i.sequence_number)}
                meta={{ copy } as QueueMeta}
                on="panel"
                density="compact"
                empty="No preview available."
              />
              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="text-xs text-on-surface-variant">
                  Preview only — Firefleet doesn&apos;t create VPN clients. Add the OpenVPN
                  client and the charger record separately.
                </p>
                <button
                  type="button" onClick={copyAll}
                  className={`${buttonClass('quiet', 'sm')} flex-shrink-0`}
                >Copy all</button>
              </div>
            </>
          )}

          {!loading && !error && loaded && items.length === 0 && (
            <EmptyState variant="line" className="py-2">No preview available.</EmptyState>
          )}
          </FadeIn>
        </div>
      </Reveal>
    </Panel>
  );
}
